import { c as _c } from "react/compiler-runtime";
import { useBuilder } from './provider';
import { groupKey, nodeAndKey, type SuggestionBuckets, useInputsSuggestions, useOutputsSuggestions, useSuggestionBuckets } from './suggestions';
import { type BundleNode, isDecisionNode, isRulesNode, isTaskNode, type Suggestion } from './types';

// Walks the bundle in the same top-to-bottom order the canvas renders so prev/next
// matches what the user sees. Mirrors canvas.tsx + task.tsx + rules/block.tsx:
// input schema → head gap → for each node (meta refinement first — it renders next to
// the name/summary at the top of the node — then field/artifact/group buckets, ghost
// rows, then node-level remove/replace) → trailing gap. Input-schema suggestions render
// at the top of the canvas (input block), so they lead the order, and final-output suggestions
// close it. Norms/skills suggestions live on other tabs and are surfaced by a tab badge rather
// than this canvas nav.
export const orderSuggestionsByCanvas = (buckets: SuggestionBuckets, nodes: BundleNode[], inputSuggestions: Suggestion[] = [], outputsSuggestions: Suggestion[] = []): Suggestion[] => {
  const out: Suggestion[] = [...inputSuggestions];
  const take = (key: string, map: Map<string, Suggestion[]>) => {
    const list = map.get(key);
    if (list) out.push(...list);
  };
  take('head', buckets.byGap);
  for (const node of nodes) {
    if (isTaskNode(node)) {
      take(node.id, buckets.nodeMetaByNode);
      for (const f of node.fields) take(nodeAndKey(node.id, f.id), buckets.byField);
      take(node.id, buckets.newFieldsByNode);
      for (const a of node.artifacts) take(nodeAndKey(node.id, a.id), buckets.byArtifact);
      take(node.id, buckets.newArtifactsByNode);
      take(node.id, buckets.byNode);
    } else if (isRulesNode(node)) {
      take(node.id, buckets.rulesMetaByNode);
      for (const g of node.groups) take(groupKey(node.id, g.id), buckets.byGroup);
      take(node.id, buckets.newGroupsByNode);
      take(node.id, buckets.byNode);
    } else if (isDecisionNode(node)) {
      take(node.id, buckets.byNode);
    }
    take(node.id, buckets.byGap);
  }
  out.push(...outputsSuggestions);
  return out;
};
export const useOrderedCanvasSuggestions = () => {
  const $ = _c(5);
  const buckets = useSuggestionBuckets();
  const nodes = useBuilder().state.bundle.nodes;
  const inputSuggestions = useInputsSuggestions();
  const outputsSuggestions = useOutputsSuggestions();
  let t0;
  if ($[0] !== buckets || $[1] !== inputSuggestions || $[2] !== nodes || $[3] !== outputsSuggestions) {
    t0 = orderSuggestionsByCanvas(buckets, nodes, inputSuggestions, outputsSuggestions);
    $[0] = buckets;
    $[1] = inputSuggestions;
    $[2] = nodes;
    $[3] = outputsSuggestions;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  return t0;
};
export const useIsSuggestionFocused = suggestionId => {
  const focused = useBuilder().state.focusedSuggestionId;
  return !!suggestionId && focused === suggestionId;
};
export const clampIndex = (i: number, len: number): number => {
  return Math.max(0, Math.min(len - 1, i));
};

// Focus the suggestion `delta` steps from `fromIndex` (clamped). This path is
// only used for explicit prev/next navigation, so it scrolls the focused card
// into view. Auto-focus/restore paths dispatch setFocusedSuggestion directly.
export const useFocusByDelta = () => {
  const $ = _c(2);
  const {
    dispatch
  } = useBuilder();
  let t0;
  if ($[0] !== dispatch) {
    t0 = (ordered, fromIndex, delta) => {
      const next = ordered[clampIndex(fromIndex + delta, ordered.length)];
      if (!next || next.id === ordered[fromIndex]?.id) {
        return false;
      }
      dispatch({
        type: "setFocusedSuggestion",
        id: next.id
      });
      dispatch({
        type: "scrollTo",
        id: next.id
      });
      return true;
    };
    $[0] = dispatch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};